import firebase from "firebase";
import {CreateAdminData, FeAdmin, FsAdmin, UpdateAdminInput, UpdateAdminResult, UpdateAdminStatusResult} from "./types";
import _ from "lodash";
import {replaceUndefinedWithDelete} from "../../../utils";

const adminDataConverter: firebase.firestore.FirestoreDataConverter<FeAdmin> = {
    fromFirestore(snapshot: firebase.firestore.QueryDocumentSnapshot<FsAdmin>): FeAdmin {
        const {createdAt, updatedAt, ...docData} = snapshot.data();
        return {
            id: snapshot.id,
            ...docData,
            createdAt: createdAt.toMillis(),
            updatedAt: updatedAt.toMillis()
        }
    },
    toFirestore({id, createdAt, updatedAt, ...docData}: FeAdmin): FsAdmin {
        return {

            ..._.pickBy(docData, _.identity) as FeAdmin,
            createdAt: firebase.firestore.Timestamp.fromMillis(createdAt),
            updatedAt: firebase.firestore.Timestamp.fromMillis(updatedAt),
        };
    }
}

export class AdminService {
    private static GetCollection() {
        return firebase.firestore()
            .collection('admins')
            .withConverter(adminDataConverter);
    }

    async createAdmin(dto: CreateAdminData): Promise<FeAdmin> {
        const result = await firebase.app()
            .functions('europe-west3')
            .httpsCallable('createAdmin')(dto);
        return {
            ...result.data as FeAdmin,
            createdAt: result.data.createdAt,
            updatedAt: result.data.updatedAt,
        }
    }

    async activateAdmin(adminId: string): Promise<UpdateAdminStatusResult> {
        const result = await firebase.app()
            .functions('europe-west3')
            .httpsCallable('updateAdminStatus')({adminId, active: true});
        return {
            ...result.data as UpdateAdminStatusResult,
            updatedAt: new Date().getTime()
        }
    }

    async deactivateAdmin(adminId: string): Promise<UpdateAdminStatusResult> {
        const result = await firebase.app()
            .functions('europe-west3')
            .httpsCallable('updateAdminStatus')({adminId, active: false});
        return {
            ...result.data as UpdateAdminStatusResult,
            updatedAt: new Date().getTime()
        }
    }

    async updateAdmin(id: string, dto: UpdateAdminInput): Promise<UpdateAdminResult> {
        await AdminService.GetCollection().doc(id).update({
            ...replaceUndefinedWithDelete(dto),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return {
            id,
            updatedAt: new Date().getTime(),
            ...dto,
        }
    }

    async listAdmins(): Promise<FeAdmin[]> {
        const querySnapshots = await AdminService.GetCollection().get();
        return querySnapshots.empty
            ? []
            : querySnapshots.docs.map(d => d.data())
    }

    async getAdmin(id: string): Promise<FeAdmin> {
        const snapshot = await AdminService.GetCollection().doc(id).get();
        const worker = snapshot.data();
        if (!worker) {
            throw new Error('Admin not found');
        }
        return worker;
    }
}

